import { useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, apiBlob, apiUpload, query } from "./api";
import type {
  FleetDomainSyncResult,
  OrgQuota,
  QuotaRequest,
  QuotaResource,
  DomainTld,
  DomainTldList,
  DomainsOverview,
  DomainOrderParams,
  FleetDomain,
  FleetDomainParams,
  StuckDomainOrder,
  InferenceCampaign,
  InferenceCampaignInput,
  InferenceCampaignRequest,
  InferenceDecisionBatch,
  BatchOutcome,
  PromoCampaign,
  PromoCampaignInput,
  PromoRedemptionRow,
  AdminApp,
  AdminAppDetail,
  AdminDatabase,
  AppSource,
  DatabaseEngine,
  AiModel,
  AiModelInput,
  AiModelList,
  AiModelSyncResult,
  Announcement,
  AnnouncementInput,
  ModelCategory,
  BillingUnit,
  AdminOrg,
  AdminOrgDetail,
  AdminUser,
  AdminUserDetail,
  AuditEntry,
  BillingOverview,
  CleanupAction,
  DeployFeedEntry,
  DeploySeries,
  DockerDiskUsage,
  EmailLogPage,
  EmailDomainDecision,
  EmailDomainPage,
  EmailDomainSyncOutcome,
  OrgRisk,
  RiskOverview,
  RiskCase,
  RiskCaseDetail,
  RiskCaseStatus,
  RiskRule,
  FleetSummary,
  GrowthSeries,
  HealthCheckResult,
  InferenceMargin,
  InferenceSeries,
  OrgBilling,
  OpenEndedPage,
  Overview,
  Paginated,
  PlacementIsolation,
  Plan,
  PlanInput,
  PlatformRole,
  RegionGroupEntry,
  RevenueSeries,
  SearchResults,
  Server,
  ServerAction,
  ServerDiagnostics,
  ServerDetail,
  ServerHardening,
  ServerMetrics,
  ServerPool,
  ServerProject,
  ServerState,
  ServerBackupStorage,
  ServerVolumes,
  VolumeRemovalResult,
  SupportAttachment,
  SupportCounts,
  SupportMessage,
  SupportTicket,
  SupportTicketDetail,
  SupportTicketFilters,
  SupportStatus,
  SupportPriority,
  SupportCategory,
  Addon,
  AddonInput,
  FleetSchedule,
  FleetTunnel,
  FleetScheduleRuns,
  FleetScheduleParams,
  DeleteWorkloadsResponse,
  PurgeOrganizationsResponse,
  Currencies,
  FxSyncResult,
  ParkedPayment,
} from "./types";

export const keys = {
  overview: ["overview"] as const,
  plans: (params: unknown) => ["plans", params] as const,
  fleetSchedules: (params: unknown) => ["fleet-schedules", params] as const,
  fleetScheduleRuns: (scheduleId: string, page: { limit: number; offset: number }) =>
    ["fleet-schedules", scheduleId, "runs", page.limit, page.offset] as const,
  addons: ["addons"] as const,
  aiModels: (params: unknown) => ["ai-models", params] as const,
  aiModel: (id: string) => ["ai-model", id] as const,
  billingOverview: ["billing", "overview"] as const,
  currencies: ["billing", "currencies"] as const,
  parkedPayments: ["billing", "parked"] as const,
  inferenceMargin: (days: number) => ["inference", "margin", days] as const,
  orgBilling: (id: string) => ["orgs", id, "billing"] as const,
  revenueSeries: (days: number) => ["reporting", "revenue", days] as const,
  inferenceSeries: (days: number) => ["reporting", "inference", days] as const,
  growthSeries: (days: number) => ["reporting", "growth", days] as const,
  deploySeries: (days: number) => ["reporting", "deploys", days] as const,
  emailLog: (params: unknown) => ["emails", params] as const,
  emailDomains: (params: unknown) => ["email-domains", params] as const,
  riskCases: (params: unknown) => ["risk", "cases", params] as const,
  riskCase: (id: string) => ["risk", "case", id] as const,
  riskRules: ["risk", "rules"] as const,
  riskOverview: (days: number) => ["risk", "overview", days] as const,
  orgRisk: (orgId: string) => ["risk", "org", orgId] as const,
  emailDomainCheck: (email: string) => ["email-domains", "check", email] as const,
  servers: (params: unknown) => ["servers", params] as const,
  regions: ["regions"] as const,
  server: (id: string) => ["servers", id] as const,
  serverProjects: (id: string) => ["servers", id, "projects"] as const,
  serverDisk: (id: string) => ["servers", id, "disk"] as const,
  serverVolumes: (id: string) => ["servers", id, "volumes"] as const,
  serverMetrics: (id: string) => ["servers", id, "metrics"] as const,
  serverDiagnostics: (id: string) => ["servers", id, "diagnostics"] as const,
  serverBackupStorage: (id: string) => ["servers", id, "backup-storage"] as const,
  users: (params: unknown) => ["users", params] as const,
  user: (id: string) => ["users", id] as const,
  communityMembership: (id: string) => ["users", id, "community"] as const,
  orgs: (params: unknown) => ["orgs", params] as const,
  org: (id: string) => ["orgs", id] as const,
  apps: (params: unknown) => ["apps", params] as const,
  databases: (params: unknown) => ["databases", params] as const,
  app: (id: string) => ["apps", id] as const,
  deployments: (params: unknown) => ["deployments", params] as const,
  audit: (params: unknown) => ["audit", params] as const,
  search: (q: string) => ["search", q] as const,
  announcements: (params: unknown) => ["announcements", params] as const,
  promoCampaigns: (params: unknown) => ["promo-campaigns", params] as const,
  promoRedemptions: (id: string, params: unknown) =>
    ["promo-campaigns", id, "redemptions", params] as const,
};

export interface ListParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export interface OrgListParams extends ListParams {
  /** Only the suspended ones — the page the bulk delete is for. */
  suspended?: boolean;
}

// --------------------------------------------------------------- overview

export function useOverview() {
  return useQuery({
    queryKey: keys.overview,
    queryFn: () => api<Overview>("/v1/admin"),
    refetchInterval: 30_000,
  });
}

// ---------------------------------------------------------------- servers

// ---------------------------------------------------------------- billing

export function useCurrencies() {
  return useQuery({
    queryKey: keys.currencies,
    queryFn: () => api<Currencies>("/v1/admin/billing/currencies"),
  });
}

export interface SetFxRateInput {
  currency: string;
  rate: number;
  note?: string;
  confirm?: boolean;
}

export function useSetFxRate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ currency, ...body }: SetFxRateInput) =>
      api<{ currency: string; previous: number; rate: number }>(`/v1/admin/billing/currencies/${currency}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.currencies });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export interface SetFxPolicyInput {
  currency: string;
  mode: "manual" | "auto";
  marginPercent?: number;
  confirm?: boolean;
}

export function useSetFxPolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ currency, ...body }: SetFxPolicyInput) =>
      api<{ currency: string; mode: string; result: FxSyncResult | null }>(`/v1/admin/billing/currencies/${currency}/policy`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.currencies });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useSyncFxRates() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ results: FxSyncResult[] }>("/v1/admin/billing/currencies/sync", { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.currencies });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/** Payments where money moved and credit could not follow, not yet closed. */
export function useParkedPayments() {
  return useQuery({
    queryKey: keys.parkedPayments,
    queryFn: () => api<{ data: ParkedPayment[] }>("/v1/admin/billing/payments/parked"),
    refetchInterval: 60_000,
  });
}

export interface SettlePaymentInput {
  paymentId: string;
  creditCents: number;
  note: string;
  password?: string;
  code?: string;
}

export function useSettlePayment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, ...body }: SettlePaymentInput) =>
      api<{ balanceMicros: number | null; restored: boolean }>(`/v1/admin/billing/payments/${paymentId}/settle`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.parkedPayments });
      void client.invalidateQueries({ queryKey: keys.billingOverview });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export interface RecordRefundInput {
  paymentId: string;
  amountCents: number;
  chargedAmount?: number;
  reference: string;
  note?: string;
  password: string;
  code: string;
}

/** A refund made outside any API, recorded after the fact -- BorderPay only. */
export function useRecordExternalRefund() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, ...body }: RecordRefundInput) =>
      api<{ balanceMicros: number; fullyRefunded: boolean }>(`/v1/admin/billing/payments/${paymentId}/refund`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.billingOverview });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useBillingOverview() {
  return useQuery({
    queryKey: keys.billingOverview,
    queryFn: () => api<BillingOverview>("/v1/admin/billing/overview"),
  });
}

/**
 * Platform-wide inference margin over a window.
 *
 * Refetched on an interval like the overview: this is a page an operator leaves
 * open while deciding whether a price needs moving, and a figure that silently
 * ages while being stared at is the one that gets acted on.
 */
export function useInferenceMargin(days: number) {
  return useQuery({
    queryKey: keys.inferenceMargin(days),
    queryFn: () => api<InferenceMargin>(`/v1/admin/inference/usage?days=${days}`),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useOrgBilling(orgId: string) {
  return useQuery({
    queryKey: keys.orgBilling(orgId),
    queryFn: () => api<OrgBilling>(`/v1/admin/billing/${orgId}`),
  });
}

// -------------------------------------------------------------- reporting

/**
 * The operator's daily series, one hook per subject.
 *
 * All share the same range allowlist and UTC-day window, and all refetch on an
 * interval: these are pages an operator leaves open while deciding something —
 * a price, a server purchase, an outreach list — and a trend that silently ages
 * while being stared at is the one that gets acted on.
 */

export function useRevenueSeries(days: number) {
  return useQuery({
    queryKey: keys.revenueSeries(days),
    queryFn: () => api<RevenueSeries>(`/v1/admin/reporting/revenue?days=${days}`),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useInferenceSeries(days: number) {
  return useQuery({
    queryKey: keys.inferenceSeries(days),
    queryFn: () => api<InferenceSeries>(`/v1/admin/reporting/inference?days=${days}`),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useGrowthSeries(days: number) {
  return useQuery({
    queryKey: keys.growthSeries(days),
    queryFn: () => api<GrowthSeries>(`/v1/admin/reporting/growth?days=${days}`),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useDeploySeries(days: number) {
  return useQuery({
    queryKey: keys.deploySeries(days),
    queryFn: () => api<DeploySeries>(`/v1/admin/reporting/deploys?days=${days}`),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

export interface EmailLogFilters extends ListParams {
  status?: "sent" | "failed" | "pending";
}

export function useEmailLog(filters: EmailLogFilters) {
  return useQuery({
    queryKey: keys.emailLog(filters),
    queryFn: () => api<EmailLogPage>(`/v1/admin/emails${query({ ...filters })}`),
  });
}

export function useRetryEmail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/v1/admin/emails/${id}/retry`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["emails"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export interface EmailDomainFilters {
  q?: string;
  source?: "operator" | "list";
  limit?: number;
}

export function useEmailDomains(filters: EmailDomainFilters) {
  return useQuery({
    queryKey: keys.emailDomains(filters),
    queryFn: () => api<EmailDomainPage>(`/v1/admin/email-domains${query({ ...filters })}`),
    placeholderData: (previous) => previous,
  });
}

/** Asked only once there is an address to ask about; an empty string asks nothing. */
export function useEmailDomainCheck(email: string) {
  return useQuery({
    queryKey: keys.emailDomainCheck(email),
    queryFn: () =>
      api<EmailDomainDecision>(`/v1/admin/email-domains/check?email=${encodeURIComponent(email)}`),
    enabled: email.length > 0,
  });
}

export function useUpsertEmailDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { domain: string; action: "block" | "allow"; reason: string }) =>
      api<{ domain: string; action: string }>("/v1/admin/email-domains", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["email-domains"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRemoveEmailDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) =>
      api<void>(`/v1/admin/email-domains/${encodeURIComponent(domain)}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["email-domains"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useSyncEmailDomains() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<EmailDomainSyncOutcome>("/v1/admin/email-domains/sync", { method: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["email-domains"] }),
  });
}

/**
 * After anything that moves a case: every risk query but the rule list. The
 * rules are code, not data -- they change with a deploy, never with a
 * decision -- and refetching them after every claim was a request, and a
 * fresh database connection on the API, for nothing.
 */
function refreshRisk(client: QueryClient) {
  return client.invalidateQueries({ queryKey: ["risk"], predicate: (query) => query.queryKey[1] !== "rules" });
}

export interface RiskCaseFilters {
  status?: RiskCaseStatus | "active" | "snoozed";
  priority?: "critical" | "high" | "normal";
  kind?: string;
  q?: string;
  mine?: boolean;
  limit?: number;
  offset?: number;
}

export function useRiskCases(filters: RiskCaseFilters) {
  return useQuery({
    queryKey: keys.riskCases(filters),
    queryFn: () =>
      api<Paginated<RiskCase>>(`/v1/admin/risk/cases${query({ ...filters, mine: filters.mine ? "1" : undefined })}`),
    placeholderData: (previous) => previous,
  });
}

/** The queue's badge: open cases, refreshed on the rail's cadence. */
export function useOpenRiskCases() {
  return useQuery({
    queryKey: keys.riskCases({ status: "active", limit: 1 }),
    queryFn: () => api<Paginated<RiskCase>>("/v1/admin/risk/cases?status=active&limit=1"),
    select: (page) => page.total,
    refetchInterval: 60_000,
  });
}

export function useRiskCase(id: string) {
  return useQuery({
    queryKey: keys.riskCase(id),
    queryFn: () => api<RiskCaseDetail>(`/v1/admin/risk/cases/${id}`),
    enabled: id.length > 0,
  });
}

export function useRiskRules() {
  return useQuery({
    queryKey: keys.riskRules,
    queryFn: () => api<{ data: RiskRule[] }>("/v1/admin/risk/rules"),
    select: (body) => new Map(body.data.map((rule) => [rule.kind, rule])),
    staleTime: 3_600_000,
  });
}

export function useOrgRisk(orgId: string) {
  return useQuery({
    queryKey: keys.orgRisk(orgId),
    queryFn: () => api<OrgRisk>(`/v1/admin/risk/organizations/${orgId}`),
    enabled: orgId.length > 0,
  });
}

export interface RiskDecisionInput {
  status: "open" | "reviewing" | "cleared" | "suspended";
  note?: string;
  /** With `open`: park it for this long. */
  snoozeHours?: number;
}

export function useDecideRiskCase(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RiskDecisionInput) =>
      api<{ id: string; status: RiskCaseStatus }>(`/v1/admin/risk/cases/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void refreshRisk(client);
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useNoteRiskCase(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api<void>(`/v1/admin/risk/cases/${id}/notes`, { method: "POST", body: { note } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.riskCase(id) }),
  });
}

/**
 * One move over several cases: claim, hand back (`open`), snooze (`open`
 * with hours), wake (`open` without), clear.
 */
export function useDecideRiskCases() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; status: "open" | "reviewing" | "cleared"; note?: string; snoozeHours?: number }) =>
      api<{ data: BatchOutcome[]; moved: number }>("/v1/admin/risk/cases/bulk", { method: "POST", body: input }),
    onSuccess: () => {
      void refreshRisk(client);
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/** One note on several cases. Support may, as on one: it decides nothing. */
export function useNoteRiskCases() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; note: string }) =>
      api<{ data: BatchOutcome[]; noted: number }>("/v1/admin/risk/cases/bulk/notes", { method: "POST", body: input }),
    onSuccess: () => {
      void refreshRisk(client);
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/**
 * Suspends the organization behind each case and closes the cases, behind
 * the operator's authenticator. Refreshes what the organization route's own
 * mutation does, since it is the same suspension.
 */
export function useSuspendRiskCases() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ otp, ...body }: { ids: string[]; reason: string; blockPayment: boolean; otp: string }) =>
      api<{ data: BatchOutcome[]; suspended: number }>("/v1/admin/risk/cases/bulk/suspend", { method: "POST", body, otp }),
    onSuccess: () => {
      void refreshRisk(client);
      void client.invalidateQueries({ queryKey: ["orgs"] });
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["audit"] });
      void client.invalidateQueries({ queryKey: ["search"] });
    },
  });
}

export function useRiskOverview(days: number) {
  return useQuery({
    queryKey: keys.riskOverview(days),
    queryFn: () => api<RiskOverview>(`/v1/admin/risk/overview?days=${days}`),
    // A monitor: kept current without anybody pressing anything.
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function useRiskSweep() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ filed: number }>("/v1/admin/risk/sweep", { method: "POST" }),
    onSuccess: () => void refreshRisk(client),
  });
}

export interface AdjustCreditInput {
  amountCents: number;
  reason: string;
  /**
   * Step-up. The route asks the operator to prove the session is still theirs
   * with their own password and a current authenticator code, the way turning
   * two-factor off does — a console tab left open is not a licence to move
   * money.
   */
  password: string;
  code: string;
}

export interface AdjustCreditResult {
  balanceMicros: number;
  restored: boolean;
  /** Whether the alert to every other operator was queued. */
  notifiedOperators: boolean;
}

export function useAdjustCredit(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AdjustCreditInput) =>
      api<AdjustCreditResult>(`/v1/admin/billing/${orgId}/adjust`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.orgBilling(orgId) });
      // The suspension state on the org page may have changed with it.
      void client.invalidateQueries({ queryKey: keys.org(orgId) });
      // Applied or refused, the attempt is a row in the audit log now.
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRecomputeBalance(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ balanceMicros: number; driftMicros: number }>(
        `/v1/admin/billing/${orgId}/recompute`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.orgBilling(orgId) });
    },
  });
}

// ------------------------------------------------------------------ plans

export function usePlans(params: ListParams = {}) {
  return useQuery({
    queryKey: keys.plans(params),
    queryFn: () => api<Paginated<Plan>>(`/v1/admin/plans${query({ ...params })}`),
  });
}

export function useCreatePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PlanInput) => api<Plan>("/v1/admin/plans", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["plans"] });
    },
  });
}

export function useUpdatePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<PlanInput> }) =>
      api<Plan>(`/v1/admin/plans/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["plans"] });
    },
  });
}

export function useDeletePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/admin/plans/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["plans"] });
    },
  });
}

// -------------------------------------------------------------- ai models

/**
 * What narrows the catalogue.
 *
 * `status` splits two questions the old unpaged list conflated. `listed` and
 * `unlisted` are the operator's own decision; `sellable` and `unsellable` are what
 * the platform can do at all — and the interesting view is usually the overlap, a
 * sellable model nobody has listed being a decision waiting to be made.
 */
export interface AiModelParams extends ListParams {
  provider?: string;
  billingUnit?: BillingUnit;
  status?: "listed" | "unlisted" | "retired" | "sellable" | "unsellable" | "unverified";
  /**
   * Variant rows such as `anthropic/claude-opus-5:batch`.
   *
   * 79 of the catalogue's 426 rows are one, each a near-duplicate of a model
   * already on the page. Hidden by default, because mixed in they are most of what
   * made a 426-row catalogue feel unmanageable.
   */
  variants?: "hide" | "only";
  category?: ModelCategory;
  sort?: "newest" | "name" | "cheapest" | "dearest" | "margin" | "sort_order";
}

/**
 * One model, for the editor page.
 *
 * Fetched rather than read out of the list's cache. The editor is a route, so it
 * can be opened from a link, a bookmark or a reload with no list ever having
 * loaded — and even when one has, the model being edited may sit on a page of it
 * that was never fetched.
 */
export function useAiModel(id: string | undefined) {
  return useQuery({
    queryKey: keys.aiModel(id ?? ""),
    queryFn: () => api<AiModel>(`/v1/admin/ai-models/${id}`),
    enabled: Boolean(id),
  });
}

export function useAiModels(params: AiModelParams = { limit: 25 }) {
  return useQuery({
    queryKey: keys.aiModels(params),
    queryFn: () => api<AiModelList>(`/v1/admin/ai-models${query({ ...params })}`),
    /*
     * Keeps the previous page on screen while the next one loads, so paging and
     * filtering redraw the table instead of replacing it with a spinner. The
     * catalogue is a comparison surface — losing the rows on every keystroke of a
     * search is what makes a paged table feel worse than the unpaged one it
     * replaced.
     */
    placeholderData: (previous) => previous,
  });
}

export function useCreateAiModel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AiModelInput) =>
      api<AiModel>("/v1/admin/ai-models", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-models"] });
    },
  });
}

/**
 * Edits a model, and shows the edit before the server has agreed to it.
 *
 * Listing, unlisting and reordering the featured ranking are one click each, and
 * they used to cost a round trip of nothing happening followed by the whole table
 * refetching. On a catalogue of sixty-one that is the difference between managing it
 * and waiting on it.
 *
 * Optimism is safe here because the failures are known and few — the API refuses an
 * unsellable listing, an unverified rate, or a rate of zero — and every one of them
 * rolls the cache back to exactly what it was. `onSettled` refetches regardless, so
 * a successful edit is reconciled with whatever else the server changed alongside it
 * (a featured move renumbers its neighbours, which no optimistic patch here tries to
 * predict).
 */
export function useUpdateAiModel() {
  const client = useQueryClient();

  return useMutation({
    // The alias carries a slash — `meta/llama-3.2-1b-instruct` — and it is a
    // path separator the API's wildcard route expects, so it is interpolated raw
    // rather than encoded. Encoding would send `%2F` and 404 every lookup.
    mutationFn: ({ id, input }: { id: string; input: AiModelInput }) =>
      api<AiModel>(`/v1/admin/ai-models/${id}`, { method: "PATCH", body: input }),

    onMutate: async ({ id, input }) => {
      // Stops an in-flight refetch from landing on top of the optimistic patch and
      // reverting it a moment before the mutation resolves.
      await client.cancelQueries({ queryKey: ["ai-models"] });

      const previous = client.getQueriesData<AiModelList>({ queryKey: ["ai-models"] });

      client.setQueriesData<AiModelList>({ queryKey: ["ai-models"] }, (page) => {
        if (!page) return page;

        const before = page.data.find((model) => model.id === id);
        const listedDelta =
          input.available === undefined || !before || before.available === input.available
            ? 0
            : input.available
              ? 1
              : -1;

        return {
          ...page,
          // Patched too, because the chips are the first thing an operator looks at
          // after listing something and a stale count reads as a failed action.
          counts: { ...page.counts, listed: page.counts.listed + listedDelta },
          data: page.data.map((model) =>
            model.id === id
              ? {
                  ...model,
                  ...(input.available !== undefined ? { available: input.available } : {}),
                  ...(input.featuredRank !== undefined
                    ? { featuredRank: input.featuredRank }
                    : {}),
                }
              : model,
          ),
        };
      });

      return { previous };
    },

    onError: (_error, _variables, context) => {
      // Every page the patch touched, back to what it was. A partial rollback would
      // leave a row claiming a state the server rejected.
      for (const [key, data] of context?.previous ?? []) {
        client.setQueryData(key, data);
      }
    },

    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["ai-models"] });
    },
  });
}

export function useDeleteAiModel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/admin/ai-models/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-models"] });
    },
  });
}

/**
 * Re-reads Cloudflare's rate card.
 *
 * Worth knowing before pressing it: sell prices are derived from the official
 * rate, so a provider re-pricing upward changes what customers pay the moment
 * this lands. The result says exactly which models moved.
 */
export function useSyncAiModels() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<AiModelSyncResult>("/v1/admin/ai-models/sync", { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-models"] });
    },
  });
}

/**
 * Defaults to a full page rather than the usual 25, because most callers are
 * server pickers that need the whole fleet in one dropdown, not a page of it.
 * 100 is the schema's ceiling; a fleet that outgrows it needs a searchable
 * picker rather than a larger number here.
 */
/** The list carries a fleet-wide summary alongside the page. */
export interface ServerPage extends Paginated<Server> {
  summary: FleetSummary;
}

export function useServers(params: ListParams = { limit: 100 }) {
  return useQuery({
    queryKey: keys.servers(params),
    queryFn: () => api<ServerPage>(`/v1/admin/servers${query({ ...params })}`),
  });
}

/** Runs the hardening sweep for one server now; the row is rewritten by the API. */
export function useCheckHardening(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ data: ServerHardening }>(`/v1/admin/servers/${serverId}/hardening`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.server(serverId) });
    },
  });
}

export function useIssueFirewallToken(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ token: string; rotated: boolean }>(`/v1/admin/servers/${serverId}/firewall-token`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.server(serverId) });
    },
  });
}

/** Shown once. The mesh agent credential for one server. */
export function issueMeshToken(serverId: string) {
  return api<{ token: string; rotated?: boolean }>(`/v1/admin/servers/${serverId}/mesh/token`, {
    method: "POST",
  });
}

export function useIssueMeshToken(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => issueMeshToken(serverId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.server(serverId) });
    },
  });
}

export function useServer(id: string) {
  return useQuery({
    queryKey: keys.server(id),
    queryFn: () => api<ServerDetail>(`/v1/admin/servers/${id}`),
    /*
     * Polled only while an engine update is running, and this is the one place
     * in the console that polls a server at all.
     *
     * An update takes minutes and finishes without anybody asking: the control
     * plane's sweep sees the new version and undrains the machine. A static
     * page through that is a page an operator refreshes by hand to find out
     * whether the thing they started worked. Fifteen seconds against a sweep
     * that runs every five minutes is oversampling on purpose — the cost is a
     * cheap indexed read, and the alternative is watching a spinner that is
     * already stale.
     *
     * It stops the moment the attempt leaves `pending`, so a server nobody is
     * updating is fetched exactly as it always was.
     */
    refetchInterval: (query) =>
      query.state.data?.update.attempt?.outcome === "pending" ? 15_000 : false,
  });
}

/**
 * The regions the API accepts, grouped as the console displays them.
 *
 * Served rather than duplicated here so there is one copy of the list. It only
 * changes when the API ships a new region, so it is cached for the session
 * instead of refetched per view — and it is kept off the `servers` key so
 * registering a server does not invalidate it.
 */
export function useRegions() {
  return useQuery({
    queryKey: keys.regions,
    queryFn: () =>
      api<{ data: RegionGroupEntry[] }>("/v1/admin/regions").then((r) => r.data),
    staleTime: Infinity,
  });
}

/**
 * Region code to its label and group.
 *
 * Both server views need this — the list to draw group headings, the detail
 * page to name the region — and neither should hold its own copy of a table
 * the API already serves.
 */
export function useRegionLookup(): Map<string, { label: string; group: string }> {
  const { data } = useRegions();

  return useMemo(() => {
    const byCode = new Map<string, { label: string; group: string }>();
    for (const entry of data ?? []) {
      for (const region of entry.regions) {
        byCode.set(region.code, { label: region.label, group: entry.group });
      }
    }
    return byCode;
  }, [data]);
}

export function useServerProjects(id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.serverProjects(id),
    queryFn: () =>
      api<{ data: ServerProject[] }>(`/v1/admin/servers/${id}/projects`).then((r) => r.data),
    enabled,
    // Live from Dokploy; no point caching it while an operator is reconciling.
    staleTime: 0,
  });
}

export interface ServerDiskResult {
  available: boolean;
  reason?: string;
  usage?: DockerDiskUsage;
}

export function useServerDiskUsage(id: string) {
  return useQuery({
    queryKey: keys.serverDisk(id),
    queryFn: () => api<ServerDiskResult>(`/v1/admin/servers/${id}/disk`),
  });
}

/**
 * Diagnostics call several Dokploy Settings endpoints, including an update
 * lookup. Keep them opt-in rather than fanning out when an operator opens an
 * ordinary server page.
 */
export function useServerDiagnostics(id: string) {
  return useQuery({
    queryKey: keys.serverDiagnostics(id),
    queryFn: () => api<ServerDiagnostics>(`/v1/admin/servers/${id}/diagnostics`),
    // This snapshot is evidence during an incident, not a live subscription.
    // It is fetched only through `refetch()` from the explicit button below.
    enabled: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useServerMetrics(id: string) {
  return useQuery({
    queryKey: keys.serverMetrics(id),
    queryFn: () => api<ServerMetrics>(`/v1/admin/servers/${id}/metrics`),
    // Load moves constantly; a figure from ten minutes ago is worse than none.
    refetchInterval: 30_000,
  });
}

export function useRunCleanup(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (action: CleanupAction) =>
      api<{ ok: true }>(`/v1/admin/servers/${id}/cleanup`, {
        method: "POST",
        body: { action },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.serverDisk(id) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/**
 * Orphaned data volumes, and the removals still queued on this server.
 *
 * A scan reads every project on the engine to learn what is still owned, so
 * it is not repeated on every window focus the way the rest of this tab is.
 * The queue rides along because it is the same question: what is left here.
 */
export function useServerVolumes(id: string) {
  return useQuery({
    queryKey: keys.serverVolumes(id),
    queryFn: () => api<ServerVolumes>(`/v1/admin/servers/${id}/volumes`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Removes orphaned volumes, each re-checked by the API first. Takes the
 * authenticator code, like every other delete of customer data here.
 */
export function useRemoveVolumes(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ names, otp }: { names: string[]; otp: string }) =>
      api<{ results: VolumeRemovalResult[] }>(`/v1/admin/servers/${id}/volumes/remove`, {
        method: "POST",
        body: { names },
        otp,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.serverVolumes(id) });
      void client.invalidateQueries({ queryKey: keys.serverDisk(id) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export interface ProvisionMonitoringResult extends Server {
  monitoring: {
    metricsUrl: string;
    port: number;
    /** False while the collector is up but has not recorded a sample yet. */
    sampling: boolean;
  };
}

/**
 * Sets monitoring up from the instance itself, rather than from two values an
 * operator pastes in. Registration already does this; this is the retry.
 */
export function useProvisionMonitoring(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<ProvisionMonitoringResult>(`/v1/admin/servers/${id}/monitoring`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["servers"] });
      void client.invalidateQueries({ queryKey: keys.server(id) });
      // The panel should try the new endpoint now, not at its next poll.
      void client.invalidateQueries({ queryKey: keys.serverMetrics(id) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRunServerAction(id: string) {
  const client = useQueryClient();
  function invalidateDiagnostics() {
    // A mutation error may still have reached Dokploy, so do not leave a stale
    // action state looking restartable. The operator explicitly refreshes it.
    void client.invalidateQueries({ queryKey: keys.serverDiagnostics(id), refetchType: "none" });
  }

  return useMutation({
    mutationFn: (action: ServerAction) =>
      api<{ accepted: true; action: ServerAction }>(`/v1/admin/servers/${id}/actions`, {
        method: "POST",
        body: { action },
    }),
    onSuccess: () => {
      // Keep the pre-restart snapshot visible. An automatic six-endpoint
      // refetch races the outage and replaces the evidence that prompted this
      // action; the operator explicitly refreshes after recovery instead.
      invalidateDiagnostics();
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: invalidateDiagnostics,
  });
}

/**
 * Asks a server to update Dokploy.
 *
 * Separate from `useRunServerAction` because the answer means something else.
 * A restart returns acceptance and the operator then judges ingress; an update
 * restarts the control plane that is answering, so this returns *dispatched*
 * and the control plane's sweep settles it by watching the version. Nothing
 * here should read as "it worked".
 *
 * Both the server row and the diagnostics are invalidated: the server has just
 * been drained for the duration, which the list shows.
 */
export function useUpdateDokploy(id: string) {
  const client = useQueryClient();
  function refresh() {
    void client.invalidateQueries({ queryKey: keys.serverDiagnostics(id), refetchType: "none" });
    void client.invalidateQueries({ queryKey: ["servers"] });
    void client.invalidateQueries({ queryKey: ["audit"] });
  }

  return useMutation({
    mutationFn: () =>
      api<{
        dispatched: true;
        attemptId: string;
        fromVersion: string | null;
        targetVersion: string;
      }>(`/v1/admin/servers/${id}/actions`, {
        method: "POST",
        body: { action: "update-dokploy" },
      }),
    onSuccess: refresh,
    // A failed dispatch may still have started the update — the request is
    // answered by a process that then restarts — so the state is refreshed on
    // both paths rather than assumed unchanged.
    onError: refresh,
  });
}

export function useRecoverServerAction(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ action, attemptId }: { action: ServerAction; attemptId: string }) =>
      api<{ action: ServerAction; attemptId: string; outcome: "recovered"; requestedAt: string }>(
        `/v1/admin/servers/${id}/actions/recover`,
        { method: "POST", body: { action, attemptId, confirm: "recover" } },
      ),
    onSuccess: (result) => {
      client.setQueryData<ServerDiagnostics>(keys.serverDiagnostics(id), (current) =>
        current
          ? {
              ...current,
              actions: {
                ...current.actions,
                restartTraefik: {
                  attemptId: result.attemptId,
                  outcome: result.outcome,
                  requestedAt: result.requestedAt,
                  requiresRecovery: false,
                  canRequestRestart: true,
                  retryAt: null,
                },
              },
            }
          : current,
      );
      // Another operator can claim a new restart as soon as this recovery
      // commits. Require a fresh snapshot before this console offers one.
      void client.invalidateQueries({ queryKey: keys.serverDiagnostics(id), refetchType: "none" });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: () => {
      // A 409 usually means another operator recovered this attempt or started
      // a newer one. Keep the snapshot as evidence, but require an explicit
      // refresh before this console can offer another acknowledgement.
      void client.invalidateQueries({ queryKey: keys.serverDiagnostics(id), refetchType: "none" });
    },
  });
}

export interface CreateServerInput {
  /** Required when the endpoint is proxied; a proxied name hides its origin. */
  originHost?: string | null;
  /**
   * Where the machine's workloads call out from. Optional: the firewall agent's
   * own poll measures one, and that is right on any ordinary single-homed box.
   * State it only where egress is routed apart from ingress.
   */
  egressHost?: string | null;
  name: string;
  url: string;
  apiKey: string;
  /** A code from `useRegions`; the API rejects anything else. */
  region?: string;
  /** Optional override of the region's point. Sent together or not at all. */
  latitude?: number | null;
  longitude?: number | null;
  notes?: string;
  maxOrgs?: number | null;
  state?: ServerState;
  pool?: ServerPool;
}

export function useCreateServer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServerInput) =>
      api<Server>("/v1/admin/servers", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["servers"] });
      void client.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

/** Unlike creation, updates may send null to clear an optional field. */
export interface UpdateServerInput {
  /** Where the machine answers directly. Null clears it back to resolving the endpoint. */
  originHost?: string | null;
  /** Where its workloads call out from. Null clears it back to what the agent observes. */
  egressHost?: string | null;
  name?: string;
  url?: string;
  apiKey?: string;
  /** Metrics URL and token must be sent together, and cleared together. */
  metricsUrl?: string | null;
  metricsToken?: string | null;
  region?: string | null;
  /** Sent together or cleared together, like the metrics pair above. */
  latitude?: number | null;
  longitude?: number | null;
  notes?: string | null;
  maxOrgs?: number | null;
  state?: ServerState;
  pool?: ServerPool;
}

export function useUpdateServer(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateServerInput) =>
      api<Server>(`/v1/admin/servers/${id}`, { method: "PATCH", body: input }),
    onSuccess: (_updated, input) => {
      void client.invalidateQueries({ queryKey: ["servers"] });
      void client.invalidateQueries({ queryKey: keys.server(id) });
      // Saving monitoring credentials should make the metrics panel try again
      // immediately, not wait out its polling interval.
      void client.invalidateQueries({ queryKey: keys.serverMetrics(id) });
      if (
        input.url !== undefined ||
        input.apiKey !== undefined ||
        input.metricsUrl !== undefined ||
        input.metricsToken !== undefined
      ) {
        // The endpoint/key changed, so prior self-hosted and administrator
        // checks are evidence about a different Dokploy control plane.
        void client.invalidateQueries({
          queryKey: keys.serverDiagnostics(id),
          refetchType: "none",
        });
      }
    },
  });
}

export function useDeleteServer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/admin/servers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["servers"] });
      void client.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useCheckServer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<HealthCheckResult>(`/v1/admin/servers/${id}/check`, { method: "POST" }),
    onSuccess: (_result, id) => {
      void client.invalidateQueries({ queryKey: ["servers"] });
      void client.invalidateQueries({ queryKey: keys.server(id) });
      void client.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

// ------------------------------------------------------------ backup storage

export function useServerBackupStorage(serverId: string) {
  return useQuery({
    queryKey: keys.serverBackupStorage(serverId),
    queryFn: () => api<{ data: ServerBackupStorage | null }>(`/v1/admin/servers/${serverId}/backup-storage`),
  });
}

export interface SaveBackupStorageInput {
  endpoint: string;
  bucket: string;
  region: string;
  accessKey: string;
  /** Omitted to keep the stored one. */
  secretAccessKey?: string;
  frequency: "every-6-hours" | "daily" | "weekly";
  keepLatest: number;
}

export function useSaveServerBackupStorage(serverId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveBackupStorageInput) =>
      api<{ data: ServerBackupStorage }>(`/v1/admin/servers/${serverId}/backup-storage`, {
        method: "PUT",
        body: input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.serverBackupStorage(serverId) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRemoveServerBackupStorage(serverId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>(`/v1/admin/servers/${serverId}/backup-storage`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.serverBackupStorage(serverId) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// ------------------------------------------------------------------ users

export function useUsers(params: ListParams) {
  return useQuery({
    queryKey: keys.users(params),
    queryFn: () => api<Paginated<AdminUser>>(`/v1/admin/users${query({ ...params })}`),
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: keys.user(id),
    queryFn: () => api<AdminUserDetail>(`/v1/admin/users/${id}`),
  });
}

export interface UpdateUserInput {
  name?: string;
  platformRole?: PlatformRole | null;
  suspended?: boolean;
}

export function useUpdateUser(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserInput) =>
      api<AdminUser>(`/v1/admin/users/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["users"] });
      void client.invalidateQueries({ queryKey: keys.user(id) });
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/**
 * The community role behind a platform account.
 *
 * The one thing this console reads from outside the control plane, and it
 * degrades rather than fails: a deployment with no community answers
 * `configured: false`, and a community that is unreachable answers
 * `unreachable`. Neither is an error, because this is one panel on a page about
 * somebody's platform account and the rest of that page is what the operator
 * came for.
 */
export interface CommunityMembership {
  configured: boolean;
  unreachable?: boolean;
  profile: {
    username: string;
    displayName: string;
    role: "member" | "moderator" | "admin";
    suspended: boolean;
  } | null;
}

export function useCommunityMembership(id: string) {
  return useQuery({
    queryKey: keys.communityMembership(id),
    queryFn: () => api<CommunityMembership>(`/v1/admin/users/${id}/community`),
  });
}

export function useSetCommunityRole(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (role: string) =>
      api<{ username: string; role: string }>(`/v1/admin/users/${id}/community/role`, {
        method: "PUT",
        body: { role },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.communityMembership(id) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRevokeSessions(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ revoked: number }>(`/v1/admin/users/${id}/revoke-sessions`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.user(id) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/** Operator removing a customer's GitHub / GitLab sign-in -- the account and its password stay. */
export function useUnlinkUserProvider(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => api<void>(`/v1/admin/users/${id}/oauth/${provider}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.user(id) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["users"] });
      void client.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

// ---------------------------------------------------------- organizations

export function useOrgs(params: OrgListParams) {
  return useQuery({
    queryKey: keys.orgs(params),
    queryFn: () =>
      api<Paginated<AdminOrg>>(
        `/v1/admin/organizations${query({ ...params, suspended: params.suspended ? "true" : undefined })}`,
      ),
  });
}

/**
 * What a delete touches, invalidated together: the tenant, every list that
 * counts its workloads, the servers that hosted them, and the audit trail the
 * delete wrote to.
 */
function afterWorkloadDelete(client: ReturnType<typeof useQueryClient>, orgId?: string) {
  if (orgId) void client.invalidateQueries({ queryKey: keys.org(orgId) });
  for (const key of ["orgs", "apps", "databases", "servers", "audit", "search"]) {
    void client.invalidateQueries({ queryKey: [key] });
  }
  void client.invalidateQueries({ queryKey: keys.overview });
}

export interface DeleteWorkloadsInput {
  appIds?: string[];
  databaseIds?: string[];
  all?: boolean;
  force?: boolean;
  /** The operator's authenticator code; every delete is stepped up. */
  otp: string;
}

/** An operator deleting some or all of one tenant's workloads. */
export function useDeleteOrgWorkloads(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ otp, ...input }: DeleteWorkloadsInput) =>
      api<DeleteWorkloadsResponse>(`/v1/admin/organizations/${orgId}/workloads/delete`, {
        method: "POST",
        body: input,
        otp,
      }),
    onSuccess: () => afterWorkloadDelete(client, orgId),
  });
}

/** Everything several tenants have, at once — the suspended page's bulk action. */
export function usePurgeOrganizations() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ otp, ...input }: { orgIds: string[]; force?: boolean; otp: string }) =>
      api<PurgeOrganizationsResponse>(`/v1/admin/organizations/workloads/delete`, {
        method: "POST",
        body: input,
        otp,
      }),
    onSuccess: () => afterWorkloadDelete(client),
  });
}

export function useOrg(id: string) {
  return useQuery({
    queryKey: keys.org(id),
    queryFn: () => api<AdminOrgDetail>(`/v1/admin/organizations/${id}`),
    // No id, no request: the limit-requests page asks for its organization
    // only when narrowed to one, and fetched `/organizations/` on every visit.
    enabled: id.length > 0,
  });
}

export interface UpdateOrgInput {
  name?: string;
  suspended?: boolean;
  suspendedReason?: string | null;
  /**
   * Refuse this organization's top-ups as well as its writes.
   *
   * Off unless asked for. Paying stays open under an ordinary suspension
   * because the workloads keep running and keep being charged, so refusing the
   * money while delivering the service is the wrong way round. This is for the
   * cases where taking it is itself the problem.
   */
  blockPayment?: boolean;
  /**
   * Pins the organization to one server, or releases it with null. Not a move:
   * the API refuses it once the organization holds any app or database, because
   * nothing that already exists can follow it.
   */
  pinnedServerId?: string | null;
}

/**
 * Calls off a closure the customer scheduled.
 *
 * One-way on purpose: an operator can undo one and cannot book one. Closing
 * somebody's tenant is not a thing support should be able to do on a call.
 */
/**
 * Moving a placement onto its own network, in the API's two steps: the
 * applications first (rolling redeploys), then the databases and tunnels
 * (each a restart), which the API refuses until every application is
 * running on its attachment. Both are safe to repeat.
 */
export function useIsolatePlacement(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { placementId: string; step: "applications" | "sealed" }) =>
      api<{ placement: string; isolation: PlacementIsolation }>(
        `/v1/admin/organizations/${orgId}/placements/${input.placementId}/network/${input.step}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.org(orgId) });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useCancelOrgClosure(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AdminOrg>(`/v1/admin/organizations/${id}/closure`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.org(id) });
      void client.invalidateQueries({ queryKey: ["orgs"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useUpdateOrg(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrgInput) =>
      api<AdminOrg>(`/v1/admin/organizations/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["orgs"] });
      void client.invalidateQueries({ queryKey: keys.org(id) });
      void client.invalidateQueries({ queryKey: ["apps"] });
      void client.invalidateQueries({ queryKey: ["servers"] });
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["audit"] });
      void client.invalidateQueries({ queryKey: ["search"] });
      // The queue shows the organization's standing on each row.
      void refreshRisk(client);
    },
  });
}

// ------------------------------------------------------------------- apps

export interface AppFilters extends ListParams {
  orgId?: string;
  serverId?: string;
  source?: AppSource;
  /** Only apps the abuse screen flagged — the queue somebody should be reading. */
  flagged?: boolean;
}

export function useApps(filters: AppFilters) {
  return useQuery({
    queryKey: keys.apps(filters),
    queryFn: () =>
      api<Paginated<AdminApp>>(
        `/v1/admin/apps${query({ ...filters, flagged: filters.flagged ? "true" : undefined })}`,
      ),
  });
}

// -------------------------------------------------------------- databases

export interface DatabaseFilters extends ListParams {
  orgId?: string;
  serverId?: string;
  engine?: DatabaseEngine;
}

/**
 * Every managed database on the platform.
 *
 * There is no `useDatabase` beside this one because there is no detail route to
 * call: the console can list them and cannot open one. An operator who needs to
 * act on a specific database still does it from the tenant it belongs to.
 */
export function useDatabases(filters: DatabaseFilters) {
  return useQuery({
    queryKey: keys.databases(filters),
    queryFn: () => api<Paginated<AdminDatabase>>(`/v1/admin/databases${query({ ...filters })}`),
  });
}

/**
 * Moves a tenant's database to another tier, on their behalf.
 *
 * Superadmin on the server for the same reason plan management is: this
 * changes what the organization pays. The change lands in the customer's own
 * audit trail marked as done by the platform.
 */
export function useChangeDatabasePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, planId }: { databaseId: string; planId: string }) =>
      api<AdminDatabase>(`/v1/admin/databases/${databaseId}`, {
        method: "PATCH",
        body: { planId },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["databases"] });
    },
  });
}

/**
 * Stops or starts a tenant's database — the operator lever for a volume
 * growing past its unenforced allowance. Lands in the customer's audit trail.
 * Stopping does not stop the tenant's billing: the volume is still on the disk,
 * and the plan is charged until the database is deleted.
 */
export function useDatabaseAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, action }: { databaseId: string; action: "start" | "stop" }) =>
      api<{ accepted: boolean }>(`/v1/admin/databases/${databaseId}/${action}`, {
        method: "POST",
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["databases"] });
    },
  });
}

function refreshDatabases(client: QueryClient) {
  void client.invalidateQueries({ queryKey: ["databases"] });
}

// POST /v1/admin/databases/:id/ha/hold with { hold: boolean }. Operator hold; does not fail over.
export function useHoldDatabaseSwitchover() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, hold }: { databaseId: string; hold: boolean }) =>
      api<unknown>(`/v1/admin/databases/${databaseId}/ha/hold`, {
        method: "POST",
        body: { hold },
      }),
    onSuccess: () => refreshDatabases(client),
  });
}

export function useSwitchDatabaseHa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (databaseId: string) =>
      api<unknown>(`/v1/admin/databases/${databaseId}/ha/switchover`, { method: "POST" }),
    onSuccess: () => refreshDatabases(client),
  });
}

export function useFailoverDatabaseHa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (databaseId: string) =>
      api<unknown>(`/v1/admin/databases/${databaseId}/ha/failover`, { method: "POST" }),
    onSuccess: () => refreshDatabases(client),
  });
}

export function useRepublishDatabaseHa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, confirmPort }: { databaseId: string; confirmPort: number | null }) =>
      api<unknown>(`/v1/admin/databases/${databaseId}/ha/republish`, {
        method: "POST",
        body: { confirmPort },
      }),
    onSuccess: () => refreshDatabases(client),
  });
}

export function useApp(id: string) {
  return useQuery({
    queryKey: keys.app(id),
    queryFn: () => api<AdminAppDetail>(`/v1/admin/apps/${id}`),
    // Follow a build while it runs, then fall back to the default cadence.
    refetchInterval: (q) =>
      q.state.data?.deployments.some((d) => d.status === "running") ? 5_000 : false,
  });
}

/** Deleting one app on the tenant's behalf; `force` carries on past an engine that refuses. */
export function useDeleteApp(id: string, orgId?: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { force?: boolean; otp: string }) =>
      api<{ deleted: true; warnings: string[] } | null>(
        `/v1/admin/apps/${id}${query({ force: input.force ? "true" : undefined })}`,
        { method: "DELETE", otp: input.otp },
      ),
    onSuccess: () => afterWorkloadDelete(client, orgId),
  });
}

export function useDeleteDatabase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, orgId, force, otp }: { databaseId: string; orgId?: string; force?: boolean; otp: string }) =>
      api<{ deleted: true; warnings: string[] } | null>(
        `/v1/admin/databases/${databaseId}${query({ force: force ? "true" : undefined })}`,
        { method: "DELETE", otp },
      ).then((result) => ({ result, orgId })),
    onSuccess: ({ orgId }) => afterWorkloadDelete(client, orgId),
  });
}

export function useAppAction(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (action: "stop" | "start" | "redeploy") =>
      api<{ accepted: boolean; rebuilding?: boolean }>(`/v1/admin/apps/${id}/${action}`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.app(id) });
      void client.invalidateQueries({ queryKey: ["apps"] });
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["deployments"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// ------------------------------------------------------- deployments, audit

export function useDeployFeed(params: ListParams) {
  return useQuery({
    queryKey: keys.deployments(params),
    queryFn: () => api<Paginated<DeployFeedEntry>>(`/v1/admin/deployments${query({ ...params })}`),
    refetchInterval: 15_000,
  });
}

export interface AuditFilters extends ListParams {
  orgId?: string;
  actorUserId?: string;
  platformOnly?: boolean;
}

export function useAudit(filters: AuditFilters) {
  return useQuery({
    queryKey: keys.audit(filters),
    queryFn: () => api<OpenEndedPage<AuditEntry>>(`/v1/admin/audit${query({ ...filters })}`),
  });
}

// ----------------------------------------------------------------- search

/**
 * The palette's query. Disabled below two characters, which is also where the
 * API stops accepting one — a single letter matches most of the database and
 * none of it usefully. Results are kept briefly so reopening the palette on the
 * same term is instant, and retries are off because a typo is not worth three
 * attempts.
 */
export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => api<SearchResults>(`/v1/admin/search${query({ q })}`),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
    retry: false,
  });
}

// ----------------------------------------------------------- announcements

/**
 * Every notice, drafts and expired ones included, newest first.
 *
 * Not filtered to what is live: the console is where a maintenance notice is
 * drafted before it runs and where last month's incident gets looked up, and
 * both would be invisible in a list showing only what a customer can see.
 */
export function useAnnouncements(params: ListParams = {}) {
  return useQuery({
    queryKey: keys.announcements(params),
    queryFn: () => api<Paginated<Announcement>>(`/v1/admin/announcements${query({ ...params })}`),
  });
}

/**
 * One notice, for the editor.
 *
 * Fetched rather than handed down from the list, because the form is a route: an
 * operator can arrive on it from a pasted URL with no list in the cache, which
 * is the point of it being a route at all.
 */
export function useAnnouncement(id: string | undefined) {
  return useQuery({
    queryKey: ["announcement", id],
    queryFn: () => api<Announcement>(`/v1/admin/announcements/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateAnnouncement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AnnouncementInput) =>
      api<Announcement>("/v1/admin/announcements", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["announcements"] });
    },
  });
}

export function useUpdateAnnouncement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<AnnouncementInput> }) =>
      api<Announcement>(`/v1/admin/announcements/${id}`, { method: "PATCH", body: input }),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: ["announcements"] });
      // The editor reads its own row, so the list alone is not enough — without
      // this, saving and reopening shows the text you just replaced.
      void client.invalidateQueries({ queryKey: ["announcement", variables.id] });
    },
  });
}

export function useDeleteAnnouncement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/v1/admin/announcements/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["announcements"] });
    },
  });
}

/* ------------------------------------------------------------- promotions */

/**
 * Every campaign, inactive and expired ones included.
 *
 * Deliberately unfiltered, matching the route: this console is where a campaign
 * is drafted and where last quarter's offer is looked up to see what it cost,
 * and both are invisible in a list showing only what a customer could redeem.
 */
/* ------------------------------------------- free-inference campaigns */

export function useInferenceCampaigns(params: ListParams = {}) {
  return useQuery({
    queryKey: ["inference-campaigns", params],
    queryFn: () =>
      api<Paginated<InferenceCampaign>>(`/v1/admin/inference-campaigns${query({ ...params })}`),
  });
}

/**
 * What the rail badge counts.
 *
 * Derived from the list rather than from an endpoint of its own: there are a
 * handful of campaigns, the figure is already on every row, and a second route
 * would be a second thing to keep in step with the first. `select` means the
 * component re-renders on the number rather than on the list behind it.
 */
export function usePendingInferenceRequests() {
  return useQuery({
    /*
     * Its own key, not a slice of the campaign list.
     *
     * This polled `?limit=100` once a minute and summed one field off it --
     * which made the server compute token totals and conversion counts for a
     * hundred campaigns to produce a single integer, measured at 684ms against
     * a database holding 800k usage rows. The endpoint answers the question
     * now, from a partial index, in about a millisecond.
     *
     * Kept under the same key prefix, so a decision that invalidates
     * ["inference-campaigns"] still refreshes the badge.
     */
    queryKey: ["inference-campaigns", "pending"],
    queryFn: () => api<{ data: { pending: number } }>("/v1/admin/inference-campaigns/pending"),
    select: (result) => result.data.pending,
    refetchInterval: 60_000,
  });
}

export function useInferenceRequests(
  campaignId: string | undefined,
  params: ListParams & { status?: string; q?: string } = {},
) {
  return useQuery({
    queryKey: ["inference-requests", campaignId, params],
    queryFn: () =>
      api<Paginated<InferenceCampaignRequest>>(
        `/v1/admin/inference-campaigns/${campaignId}/requests${query({ ...params })}`,
      ),
    enabled: Boolean(campaignId),
  });
}

/**
 * The same verdict on several requests, in one call.
 *
 * One request rather than a loop of the single mutation. The server decides
 * them in order under the campaign's lock, which is what keeps two approvals
 * from both reading the same remaining-keys count -- a client-side loop would
 * be racing that lock from outside it, and would report "failed" for rows the
 * server had merely queued behind itself.
 */
// ---------------------------------------------------------------- quotas

/** The queue of requests to raise a ceiling, or the history of decided ones. */
export function useQuotaRequests(
  status: "requested" | "approved" | "denied" | undefined,
  limit = 50,
  orgId?: string,
  /** Off until it is looked at: a history tab nobody opened is a request, and an API connection, spent. */
  enabled = true,
) {
  return useQuery({
    queryKey: ["quota-requests", status ?? "all", limit, orgId ?? "all"],
    queryFn: () =>
      api<{ data: QuotaRequest[]; total: number }>(
        `/v1/admin/quota-requests?limit=${limit}${status ? `&status=${status}` : ""}${orgId ? `&orgId=${orgId}` : ""}`,
      ),
    enabled,
  });
}

/** How many are waiting, for the rail's badge. One integer from a partial index. */
export function usePendingQuotaRequests() {
  return useQuery({
    queryKey: ["quota-requests", "pending"],
    queryFn: () =>
      api<{ data: { pending: number } }>("/v1/admin/quota-requests/pending").then((r) => r.data.pending),
    refetchInterval: 60_000,
  });
}

export function useDecideQuotaRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; decision: "approved" | "denied"; grantedLimit?: number; note?: string }) =>
      api<{ data: QuotaRequest }>(`/v1/admin/quota-requests/${id}`, { method: "POST", body }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["quota-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["org-quota", result.data.orgId] });
      void queryClient.invalidateQueries({ queryKey: keys.org(result.data.orgId) });
    },
  });
}

/**
 * The same decision on several requests. Approval grants what each asked
 * for; one that should get less is decided on its own, in the modal above.
 */
export function useDecideQuotaRequests() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[]; decision: "approved" | "denied"; note?: string }) =>
      api<{ data: BatchOutcome[]; decided: number }>("/v1/admin/quota-requests/bulk", { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quota-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["org-quota"] });
      void queryClient.invalidateQueries({ queryKey: ["orgs"] });
    },
  });
}

/** An organization's ceilings and usage, on its page. */
export function useOrgQuota(orgId: string) {
  return useQuery({
    queryKey: ["org-quota", orgId],
    queryFn: () => api<{ data: OrgQuota }>(`/v1/admin/organizations/${orgId}/quota`).then((r) => r.data),
  });
}

export function useSetOrgQuota(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Record<QuotaResource, number | null>>) =>
      api<{ data: OrgQuota }>(`/v1/admin/organizations/${orgId}/quota`, { method: "PATCH", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org-quota", orgId] });
    },
  });
}

export function useDecideInferenceRequests(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      requestIds: string[];
      decision: "approved" | "rejected";
      note?: string;
    }) =>
      api<{ data: InferenceDecisionBatch }>(
        `/v1/admin/inference-campaigns/${campaignId}/requests`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["inference-requests", campaignId] });
      void queryClient.invalidateQueries({ queryKey: ["inference-campaigns"] });
    },
  });
}

export function useSaveInferenceCampaign(campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InferenceCampaignInput) =>
      api<{ data: InferenceCampaign }>(
        campaignId
          ? `/v1/admin/inference-campaigns/${campaignId}`
          : "/v1/admin/inference-campaigns",
        { method: campaignId ? "PATCH" : "POST", body: payload },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["inference-campaigns"] });
    },
  });
}

/**
 * Approving or turning down one request.
 *
 * Both invalidations matter and for different reasons: the queue because the
 * row has moved out of the tab being looked at, and the campaign list because
 * an approval moves `keysIssued` and the badge above it.
 */
export function useDecideInferenceRequest(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      ...body
    }: {
      requestId: string;
      decision: "approved" | "rejected";
      note?: string;
    }) =>
      api<{ data: { id: string; status: string } }>(
        `/v1/admin/inference-campaigns/${campaignId}/requests/${requestId}`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["inference-requests", campaignId] });
      void queryClient.invalidateQueries({ queryKey: ["inference-campaigns"] });
    },
  });
}

export function usePromoCampaigns(params: ListParams = {}) {
  return useQuery({
    queryKey: keys.promoCampaigns(params),
    queryFn: () => api<Paginated<PromoCampaign>>(`/v1/admin/promotions${query({ ...params })}`),
  });
}

/**
 * Who redeemed one, and whether they went on to pay.
 *
 * The `converted` column is the reason this exists as its own read rather than a
 * count on the list: a campaign with a hundred redemptions and no subsequent
 * top-ups performed differently from one with ten redemptions and eight paying
 * customers, and the totals are identical.
 */
export function usePromoRedemptions(campaignId: string | undefined, params: ListParams = {}) {
  return useQuery({
    enabled: Boolean(campaignId),
    queryKey: keys.promoRedemptions(campaignId ?? "", params),
    queryFn: () =>
      api<Paginated<PromoRedemptionRow>>(
        `/v1/admin/promotions/${campaignId}/redemptions${query({ ...params })}`,
      ),
  });
}

export function useCreatePromoCampaign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PromoCampaignInput) =>
      api<PromoCampaign>("/v1/admin/promotions", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["promo-campaigns"] });
    },
  });
}

export function useUpdatePromoCampaign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<PromoCampaignInput> & { id: string }) =>
      api<PromoCampaign>(`/v1/admin/promotions/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["promo-campaigns"] });
    },
  });
}

/**
 * Deletes a campaign nobody has redeemed.
 *
 * The API refuses one that has been, and the console does not pre-empt that: the
 * refusal explains why — those redemptions are what a customer's ledger line
 * points at — and an operator who wanted it gone wants deactivation instead.
 */
export function useDeletePromoCampaign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/v1/admin/promotions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["promo-campaigns"] });
    },
  });
}

/**
 * Asks the API for a code nothing is using.
 *
 * Server-side rather than generated here, because uniqueness is a property of
 * the table: this console can only check the page it happens to have loaded, and
 * the operator creating a campaign is exactly the person who would not notice a
 * collision until the insert failed.
 *
 * No cache invalidation — it reserves nothing and creates nothing, so there is
 * no list to refresh.
 */
export function useGeneratePromoCode() {
  return useMutation({
    mutationFn: () => api<{ code: string }>("/v1/admin/promotions/code", { method: "POST" }),
  });
}

// --------------------------------------------------------------- support

/**
 * The queue, and the counts above it.
 *
 * Filters are part of the key, so moving between tabs serves a cached slice and
 * coming back does not refetch — the same reason the usage report keys on its
 * range.
 */
export function useSupportTickets(filters: SupportTicketFilters = {}) {
  return useQuery({
    queryKey: ["support", "tickets", filters],
    queryFn: () =>
      api<Paginated<SupportTicket>>(`/v1/admin/tickets${query({ ...filters })}`),
  });
}

/**
 * What the rail badge shows, and what the queue's tabs count.
 *
 * Polled: a console left open on the queue is the one screen where a number
 * going stale is the failure — an operator watching an empty queue while a
 * ticket sits in it is exactly what this feature exists to prevent.
 */
export function useSupportCounts() {
  return useQuery({
    queryKey: ["support", "counts"],
    queryFn: () => api<SupportCounts>("/v1/admin/tickets/counts"),
    refetchInterval: 60_000,
  });
}

export function useSupportTicket(id: string | undefined) {
  return useQuery({
    queryKey: ["support", "ticket", id],
    queryFn: () => api<SupportTicketDetail>(`/v1/admin/tickets/${id}`),
    enabled: Boolean(id),
  });
}

/** A reply the customer reads, or a note only this console shows. */
export function useReplyToTicket(ticketId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; internal: boolean; status?: SupportStatus }) =>
      api<SupportMessage>(`/v1/admin/tickets/${ticketId}/messages`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support"] });
    },
  });
}

export function useUpdateTicket(ticketId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      status?: SupportStatus;
      priority?: SupportPriority;
      category?: SupportCategory;
      assignedTo?: string | null;
    }) => api<SupportTicket>(`/v1/admin/tickets/${ticketId}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support"] });
    },
  });
}

export function useUploadTicketAttachment(ticketId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, file }: { messageId: string; file: File }) =>
      apiUpload<SupportAttachment>(
        `/v1/admin/tickets/${ticketId}/messages/${messageId}/attachments`,
        file,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support", "ticket", ticketId] });
    },
  });
}

/**
 * One move over several tickets from the queue: take or release them, set
 * their status or their impact. A customer is mailed exactly when one ticket
 * at a time would have mailed them — a move to resolved or closed.
 */
export function useUpdateTickets() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ids: string[];
      status?: SupportStatus;
      priority?: SupportPriority;
      assignedTo?: string | null;
    }) => api<{ data: BatchOutcome[]; updated: number }>("/v1/admin/tickets/bulk", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support"] });
    },
  });
}

/** The same reply, or the same note, on several tickets — each on its own thread. */
export function useReplyToTickets() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; body: string; internal: boolean; status?: SupportStatus }) =>
      api<{ data: BatchOutcome[]; sent: number }>("/v1/admin/tickets/bulk/messages", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support"] });
    },
  });
}

/** Deletes a ticket, its thread and its files. Spam, or a pasted credential. */
export function useDeleteTicket() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/admin/tickets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["support"] });
    },
  });
}

/** An attachment's bytes, fetched with the operator's token when asked for. */
export function fetchTicketAttachment(ticketId: string, attachmentId: string) {
  return apiBlob(`/v1/admin/tickets/${ticketId}/attachments/${attachmentId}`);
}

// ------------------------------------------------------------- schedules

/**
 * Every scheduled job on the fleet.
 *
 * Read-only: an operator does not edit a tenant's cron, and there is no support
 * case where they should. This exists for the question a busy machine raises —
 * which tenant is running what, on which box.
 */
export interface FleetTunnelParams {
  orgId?: string;
  serverId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Every private connection on the fleet.
 *
 * Read-only, and the one operator surface for a feature whose failures are
 * otherwise answered by reading Postgres by hand.
 */
export function useFleetTunnels(params: FleetTunnelParams = {}) {
  return useQuery({
    queryKey: ["fleet-tunnels", params] as const,
    queryFn: () => api<Paginated<FleetTunnel>>(`/v1/admin/tunnels${query({ ...params })}`),
  });
}

export function useFleetSchedules(params: FleetScheduleParams = {}) {
  return useQuery({
    queryKey: keys.fleetSchedules(params),
    queryFn: () => api<Paginated<FleetSchedule>>(`/v1/admin/schedules${query({ ...params })}`),
  });
}

/**
 * One job's run history, fetched only when an operator opens it.
 *
 * Not folded into the fleet list: it is an engine round trip per job, and a
 * page of twenty-five would make twenty-five of them before showing anything.
 */
export function useFleetScheduleRuns(
  scheduleId: string | null,
  page: { limit: number; offset: number } = { limit: 10, offset: 0 },
) {
  return useQuery({
    queryKey: keys.fleetScheduleRuns(scheduleId ?? "", page),
    queryFn: () =>
      api<FleetScheduleRuns>(
        `/v1/admin/schedules/${scheduleId!}/runs?limit=${page.limit}&offset=${page.offset}`,
      ),
    enabled: Boolean(scheduleId),
    // The page already open stays put while the next one loads.
    placeholderData: keepPreviousData,
  });
}

export function useAddons() {
  return useQuery({
    queryKey: keys.addons,
    queryFn: () => api<{ data: Addon[] }>("/v1/admin/schedules/addons"),
  });
}

/**
 * Re-pricing an add-on, which changes what every tenant holding one pays.
 *
 * The API answers a `notice` when the new price is higher than the old one —
 * §6.5 of the Terms commits to 30 days' notice before a price rise, and the
 * monthly cap for the month already in progress rises with it. Surfaced rather
 * than swallowed, because this is the one screen where somebody could trigger
 * that obligation without meaning to.
 */
export function useUpdateAddon() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: AddonInput & { id: string }) =>
      api<Addon & { notice?: string }>(`/v1/admin/schedules/addons/${id}`, {
        method: "PATCH",
        body: input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.addons });
      void client.invalidateQueries({ queryKey: ["fleet-schedules"] });
    },
  });
}

// ------------------------------------------------------------------ domains

export function useDomainsOverview() {
  return useQuery({
    queryKey: ["domains-overview"] as const,
    queryFn: () => api<DomainsOverview>("/v1/admin/domains/overview"),
  });
}

export function useEnableSellableTlds() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ data: { enabled: string[] } }>("/v1/admin/domains/tlds/enable-sellable", { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["domain-tlds"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
    },
  });
}

export function useDomainOrders(params: DomainOrderParams = {}) {
  return useQuery({
    queryKey: ["domain-orders", params] as const,
    queryFn: () => api<Paginated<StuckDomainOrder>>(`/v1/admin/domains/orders${query({ ...params })}`),
  });
}

export function useSyncFleetDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => api<{ data: FleetDomain }>(`/v1/admin/domains/${domainId}/sync`, { method: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["fleet-domains"] }),
  });
}

export function useDomainTlds() {
  return useQuery({
    queryKey: ["domain-tlds"] as const,
    queryFn: () => api<DomainTldList>("/v1/admin/domains/tlds"),
  });
}

export function useUpdateDomainTld() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ extension, input }: { extension: string; input: { enabled?: boolean; markupBps?: number | null } }) =>
      api<{ data: DomainTld }>(`/v1/admin/domains/tlds/${encodeURIComponent(extension)}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["domain-tlds"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useAddDomainTld() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (extension: string) =>
      api<{ data: DomainTld }>("/v1/admin/domains/tlds", { method: "POST", body: { extension } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["domain-tlds"] }),
  });
}

export function useSyncDomainTlds() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ data: { scanned: number; repriced: string[]; added: string[]; unsellable: { extension: string; reason: string }[] } }>(
        "/v1/admin/domains/tlds/sync",
        { method: "POST" },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["domain-tlds"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
    },
  });
}

export function useFleetDomains(params: FleetDomainParams = {}) {
  return useQuery({
    queryKey: ["fleet-domains", params] as const,
    queryFn: () => api<Paginated<FleetDomain>>(`/v1/admin/domains${query({ ...params })}`),
  });
}

/** Re-reads up to a page of names at the registrar, one call each; reports per name. */
export function useBulkSyncFleetDomains() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api<{ data: FleetDomainSyncResult[]; synced: number }>("/v1/admin/domains/bulk/sync", { method: "POST", body: { ids } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["fleet-domains"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
    },
  });
}

/** Clears the abuse flags on up to a page of names. */
export function useBulkReviewFleetDomains() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api<{ data: FleetDomain[]; reviewed: number }>("/v1/admin/domains/bulk/review", { method: "POST", body: { ids } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["fleet-domains"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
      void client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

/** The same change on many extensions: on or off sale, and/or one markup. */
export function useBulkUpdateDomainTlds() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { extensions: string[]; enabled?: boolean; markupBps?: number | null }) =>
      api<{ data: DomainTld[]; updated: number }>("/v1/admin/domains/tlds/bulk", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["domain-tlds"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useReviewFleetDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => api<{ data: FleetDomain }>(`/v1/admin/domains/${domainId}/review`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["fleet-domains"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
      void client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

export function useStuckDomainOrders() {
  return useQuery({
    queryKey: ["domain-orders-stuck"] as const,
    queryFn: () => api<{ data: StuckDomainOrder[] }>("/v1/admin/domains/orders/stuck"),
  });
}

export function useSettleDomainOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, outcome, openproviderId, otp }: { orderId: string; outcome: "completed" | "reversed"; openproviderId?: number; otp: string }) =>
      api<{ data: StuckDomainOrder }>(`/v1/admin/domains/orders/${orderId}/settle`, {
        method: "POST",
        body: { outcome, ...(openproviderId ? { openproviderId } : {}) },
        otp,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["domain-orders-stuck"] });
      void client.invalidateQueries({ queryKey: ["domain-orders"] });
      void client.invalidateQueries({ queryKey: ["domains-overview"] });
      void client.invalidateQueries({ queryKey: ["fleet-domains"] });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}
